/*
 * `/invite#<id>.<signature>` (flow F3). The page asks the server what the link is for, then takes one
 * of three paths: a new person sets a name and password; a person with an account signs in with the
 * address fixed; a person signed in as someone else is told so and can sign out. Every refusal the
 * server can give a link has its own words (`account-copy.ts`). The fragment stays in the address, so
 * a reload, or the "finish joining" path after a partial failure, still has the link.
 */
import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import { appDate } from "../../src/app-dates.ts";
import {
  acceptFailure,
  endsTheLink,
  LIMITED,
  type LinkProblem,
  linkProblem,
  passwordProblem,
  roleLabel,
  UNREACHABLE,
} from "./account-copy.ts";
import {
  AuthFrame,
  errorCode,
  NewPasswordFields,
  postJson,
  type RouteAnswer,
} from "./auth-frame.tsx";
import { fragmentToken } from "./reset-password.tsx";
import { failureMessage, type SignInFailure, signInOutcome } from "./sign-in-copy.ts";

const InspectSchema = z.object({
  invitation: z.object({ email: z.string(), role: z.string(), expiresAt: z.coerce.date() }),
  organizationName: z.string(),
  workspaces: z.array(z.object({ id: z.string(), name: z.string() })).min(1),
  hasAccount: z.boolean(),
});
type Invitation = z.infer<typeof InspectSchema>;
const SessionSchema = z.object({ user: z.object({ email: z.string() }) }).nullable();

type View =
  | { readonly kind: "checking" }
  | { readonly kind: "problem"; readonly problem: LinkProblem }
  | { readonly kind: "unreachable" }
  | { readonly kind: "ready"; readonly invitation: Invitation; readonly signedInAs: string | null };

async function readSession(): Promise<string | null | undefined> {
  try {
    const response = await fetch("/api/auth/get-session", { credentials: "same-origin" });
    if (!response.ok) return undefined;
    const session = SessionSchema.safeParse(await response.json());
    return session.success ? (session.data?.user.email.toLowerCase() ?? null) : undefined;
  } catch {
    return undefined;
  }
}

async function inspect(token: string): Promise<View> {
  const [answer, signedInAs] = await Promise.all([
    postJson("/api/account/invitations/inspect", { token }),
    readSession(),
  ]);
  if (answer.status === 0 || answer.status >= 500 || signedInAs === undefined)
    return { kind: "unreachable" };
  if (answer.status !== 200)
    return { kind: "problem", problem: linkProblem(errorCode(answer.body)) };
  const invitation = InspectSchema.safeParse(answer.body);
  if (!invitation.success) return { kind: "problem", problem: linkProblem(null) };
  return { kind: "ready", invitation: invitation.data, signedInAs };
}

/** "Join Brightwater" for one workspace; the agency's name when the invitation opens several. */
function joinHeading(invitation: Pick<Invitation, "organizationName" | "workspaces">): string {
  const [only, ...rest] = invitation.workspaces;
  return only && rest.length === 0 ? `Join ${only.name}` : `Join ${invitation.organizationName}`;
}

function Facts(props: { invitation: Invitation }) {
  const { invitation } = props;
  return (
    <dl className="lcr-auth-facts">
      <dt>Agency</dt>
      <dd>{invitation.organizationName}</dd>
      <dt>{invitation.workspaces.length === 1 ? "Workspace" : "Workspaces"}</dt>
      <dd>{invitation.workspaces.map((workspace) => workspace.name).join(", ")}</dd>
      <dt>Role</dt>
      <dd>{roleLabel(invitation.invitation.role)}</dd>
      <dt>Invited address</dt>
      <dd>{invitation.invitation.email}</dd>
      <dt>Works until</dt>
      <dd>{appDate(invitation.invitation.expiresAt.toISOString())}</dd>
    </dl>
  );
}

/** After joining, the app opens on its own: the session cookie came back with the answer. */
const openApp = () => location.assign("/");

/**
 * Where a refused accept goes: a refusal that ends the link replaces the page with its heading; any
 * other stays on the form as a sentence under the button.
 */
function acceptRefusal(
  answer: RouteAnswer,
): { readonly ends: string } | { readonly failure: string } {
  const code = errorCode(answer.body);
  if (answer.status === 0) return { failure: UNREACHABLE };
  if (answer.status === 429) return { failure: LIMITED };
  if (code !== null && endsTheLink(code)) return { ends: code };
  return { failure: acceptFailure(code) };
}

const HAS_ACCOUNT = new Set(["invitation_partial_failure_sign_in", "invitation_sign_in_required"]);

export function AcceptInvite() {
  const [token, setToken] = useState(fragmentToken);
  const [view, setView] = useState<View>({ kind: "checking" });
  // A second invitation link opened in this tab changes only the fragment: no new page load.
  useEffect(() => {
    const follow = () => setToken(fragmentToken());
    window.addEventListener("hashchange", follow);
    return () => window.removeEventListener("hashchange", follow);
  }, []);
  const check = useCallback(async () => {
    if (token === null) {
      setView({ kind: "problem", problem: linkProblem(null) });
      return;
    }
    setView({ kind: "checking" });
    setView(await inspect(token));
  }, [token]);
  useEffect(() => {
    void check();
  }, [check]);

  if (view.kind === "checking")
    return (
      <AuthFrame tab="Invitation" title="Checking your invitation…">
        <p className="lcr-signin-sub" role="status">
          This takes a moment.
        </p>
      </AuthFrame>
    );
  if (view.kind === "unreachable")
    return (
      <AuthFrame tab="Invitation" title="Could not check this invitation">
        <p className="lcr-signin-sub" role="alert">
          {UNREACHABLE}
        </p>
        <div className="lcr-signin-form">
          <button type="button" className="lcr-small-button lcr-auth-retry" onClick={check}>
            Try again
          </button>
        </div>
      </AuthFrame>
    );
  if (view.kind === "problem")
    return (
      <AuthFrame tab="Invitation" title={view.problem.title}>
        <p className="lcr-signin-sub">{view.problem.detail}</p>
        <div className="lcr-signin-form">
          <a className="lcr-linklike" href="/">
            Go to sign in
          </a>
        </div>
      </AuthFrame>
    );
  return (
    <Ready
      token={token ?? ""}
      invitation={view.invitation}
      signedInAs={view.signedInAs}
      onProblem={(code) => setView({ kind: "problem", problem: linkProblem(code) })}
      onRecheck={check}
    />
  );
}

function Ready(props: {
  token: string;
  invitation: Invitation;
  signedInAs: string | null;
  onProblem: (code: string | null) => void;
  onRecheck: () => Promise<void>;
}) {
  const { invitation } = props;
  const invited = invitation.invitation.email.toLowerCase();
  // A partial failure made the account, so the same link now finishes by signing in.
  const [hasAccount, setHasAccount] = useState(invitation.hasAccount);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** Joins with the session this browser holds now. */
  const acceptSignedIn = async (): Promise<void> => {
    const answer = await postJson("/api/account/invitations/accept-signed-in", {
      token: props.token,
    });
    if (answer.status === 200) return openApp();
    const refusal = acceptRefusal(answer);
    if ("ends" in refusal) props.onProblem(refusal.ends);
    else setFailure(refusal.failure);
  };

  const sub = (
    <>
      <span className="lcr-auth-lead">You were invited to LeadCue.</span>
      <Facts invitation={invitation} />
    </>
  );

  if (props.signedInAs !== null && props.signedInAs !== invited)
    return (
      <AuthFrame tab="Invitation" title={joinHeading(invitation)} sub={sub}>
        <div className="lcr-signin-form">
          <p className="lcr-auth-notice" role="alert">
            This invitation is for <strong>{invitation.invitation.email}</strong>. You are signed in
            as <strong>{props.signedInAs}</strong>. Sign out, then accept it as{" "}
            {invitation.invitation.email}.
          </p>
          <button
            type="button"
            className="lcr-primary lcr-signin-submit"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              await postJson("/api/auth/sign-out", {});
              await props.onRecheck();
            }}
          >
            {busy ? "Signing out…" : "Sign out"}
          </button>
        </div>
      </AuthFrame>
    );

  if (props.signedInAs === invited)
    return (
      <AuthFrame tab="Invitation" title={joinHeading(invitation)} sub={sub}>
        <div className="lcr-signin-form">
          {failure && (
            <p className="lcr-signin-alert" role="alert">
              {failure}
            </p>
          )}
          <button
            type="button"
            className="lcr-primary lcr-signin-submit"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setFailure(null);
              await acceptSignedIn();
              setBusy(false);
            }}
          >
            {busy ? "Joining…" : "Accept the invitation"}
          </button>
        </div>
      </AuthFrame>
    );

  if (hasAccount)
    return (
      <AuthFrame tab="Invitation" title={joinHeading(invitation)} sub={sub}>
        <SignInToAccept
          email={invitation.invitation.email}
          failure={failure}
          onFailure={setFailure}
          onSignedIn={acceptSignedIn}
        />
      </AuthFrame>
    );

  return (
    <AuthFrame tab="Invitation" title={joinHeading(invitation)} sub={sub}>
      <NewAccount
        token={props.token}
        failure={failure}
        onFailure={setFailure}
        onProblem={props.onProblem}
        onMadeAccount={() => setHasAccount(true)}
      />
    </AuthFrame>
  );
}

function NewAccount(props: {
  token: string;
  failure: string | null;
  onFailure: (message: string | null) => void;
  onProblem: (code: string | null) => void;
  onMadeAccount: () => void;
}) {
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [invalid, setInvalid] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <form
      className="lcr-signin-form"
      noValidate
      onSubmit={async (event) => {
        event.preventDefault();
        const problem =
          name.trim() === ""
            ? acceptFailure("invitation_name_required")
            : passwordProblem(password, confirm);
        setInvalid(problem !== null && name.trim() !== "");
        props.onFailure(problem);
        if (problem) return;
        setBusy(true);
        const answer = await postJson("/api/account/invitations/accept", {
          token: props.token,
          name: name.trim(),
          password,
        });
        setBusy(false);
        if (answer.status === 200) return openApp();
        // The account exists now (made just before a later step failed, or earlier): sign in instead.
        if (HAS_ACCOUNT.has(errorCode(answer.body) ?? "")) props.onMadeAccount();
        const refusal = acceptRefusal(answer);
        if ("ends" in refusal) props.onProblem(refusal.ends);
        else props.onFailure(refusal.failure);
      }}
    >
      <label className="lcr-signin-field">
        Your name
        <input
          type="text"
          autoComplete="name"
          required
          maxLength={200}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </label>
      <NewPasswordFields
        label="Choose a password"
        password={password}
        confirm={confirm}
        onPassword={setPassword}
        onConfirm={setConfirm}
        invalid={invalid}
        describedBy={props.failure ? "lcr-accept-failure" : undefined}
      />
      {props.failure && (
        <p id="lcr-accept-failure" className="lcr-signin-alert" role="alert">
          {props.failure}
        </p>
      )}
      <button className="lcr-primary lcr-signin-submit" type="submit" disabled={busy}>
        {busy ? "Joining…" : "Accept and set a password"}
      </button>
    </form>
  );
}

function SignInToAccept(props: {
  email: string;
  failure: string | null;
  onFailure: (message: string | null) => void;
  onSignedIn: () => Promise<void>;
}) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [rejected, setRejected] = useState(false);
  return (
    <form
      className="lcr-signin-form"
      onSubmit={async (event) => {
        event.preventDefault();
        setBusy(true);
        props.onFailure(null);
        const answer = await postJson("/api/auth/sign-in/email", { email: props.email, password });
        const outcome = signInOutcome(answer.status, answer.retryAfter);
        setRejected(outcome.kind === "rejected");
        if (outcome.kind === "signed_in") await props.onSignedIn();
        else props.onFailure(failureMessage(outcome satisfies SignInFailure));
        setBusy(false);
      }}
    >
      <p className="lcr-auth-hint">This address already has an account. Sign in to accept.</p>
      <label className="lcr-signin-field">
        Email
        <input type="email" autoComplete="username" value={props.email} readOnly />
      </label>
      <div className="lcr-signin-field">
        <span className="lcr-signin-row">
          <label htmlFor="lcr-accept-password">Password</label>
          <a className="lcr-linklike" href="/forgot-password">
            Forgot password?
          </a>
        </span>
        <input
          id="lcr-accept-password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          aria-invalid={rejected || undefined}
          aria-describedby={props.failure ? "lcr-accept-failure" : undefined}
          onChange={(event) => setPassword(event.target.value)}
        />
      </div>
      {props.failure && (
        <p id="lcr-accept-failure" className="lcr-signin-alert" role="alert">
          {props.failure}
        </p>
      )}
      <button className="lcr-primary lcr-signin-submit" type="submit" disabled={busy}>
        {busy ? "Joining…" : "Sign in and accept"}
      </button>
    </form>
  );
}
